<template>
    <div
        class="segmented-toggle toggle-switch"
        :class="{ 'is-disabled': isDisabled, 'is-square': mergedOptions.layout.squareCorners }"
        :style="rootStyle"
        role="radiogroup"
        :aria-disabled="String(isDisabled)">
        <button
            v-for="label in mergedOptions.items.labels"
            :key="label.name"
            type="button"
            class="segmented-toggle__item"
            :class="{ 'is-selected': selectedItem === label.name }"
            :style="selectedItem === label.name ? getSelectedItemStyle(label) : itemStyle"
            :disabled="isDisabled"
            @click="select(label.name, $event)">
            {{ label.name }}
        </button>
    </div>
</template>

<script>
function defaultOptions() {
    return {
        layout: {
            backgroundColor: "lightgray",
            borderColor: "gray",
            color: "black",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: "500",
            fontWeightSelected: "700",
            noBorder: false,
            selectedBackgroundColor: "green",
            selectedColor: "white",
            squareCorners: false,
        },
        size: {
            fontSize: 1.5,
            height: 3.25,
            padding: 0.5,
            width: 10,
        },
        items: {
            delay: 0.4,
            disabled: false,
            labels: [
                { name: "Off", color: "white", backgroundColor: "red" },
                { name: "On", color: "white", backgroundColor: "green" },
            ],
            preSelected: "unknown",
        },
    };
}

function mergeOptions(base, options) {
    if (!options || typeof options !== "object") {
        return base;
    }

    return {
        layout: {
            ...base.layout,
            ...(options.layout || {}),
        },
        size: {
            ...base.size,
            ...(options.size || {}),
        },
        items: {
            ...base.items,
            ...(options.items || {}),
            labels: Array.isArray(options.items?.labels) ? options.items.labels : [...base.items.labels],
        },
    };
}

export default {
    name: "SegmentedToggle",
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        group: {
            type: String,
            default: "",
        },
        modelValue: {
            type: String,
            default: undefined,
        },
        name: {
            type: String,
            default: undefined,
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        value: {
            type: String,
            default: undefined,
        },
    },
    data() {
        return {
            selectedItem: "unknown",
        };
    },
    computed: {
        mergedOptions() {
            return mergeOptions(defaultOptions(), this.options);
        },
        isDisabled() {
            return this.disabled || this.mergedOptions.items.disabled;
        },
        selectedValue() {
            return this.modelValue !== undefined ? this.modelValue : this.value;
        },
        rootStyle() {
            return {
                "--segmented-border-color": this.mergedOptions.layout.noBorder ? "transparent" : this.mergedOptions.layout.borderColor,
                "--segmented-height": `${this.mergedOptions.size.height}rem`,
                "--segmented-padding": `${this.mergedOptions.size.padding}rem`,
                "--segmented-transition": `${this.mergedOptions.items.delay}s`,
                height: `${this.mergedOptions.size.height}rem`,
                maxWidth: "100%",
                width: `${this.mergedOptions.size.width}rem`,
            };
        },
        itemStyle() {
            return {
                backgroundColor: this.mergedOptions.layout.backgroundColor,
                color: this.mergedOptions.layout.color,
                fontFamily: this.mergedOptions.layout.fontFamily,
                fontSize: `${this.mergedOptions.size.fontSize}rem`,
                fontWeight: this.mergedOptions.layout.fontWeight,
                width: `${this.mergedOptions.size.width}rem`,
            };
        },
    },
    created() {
        this.syncSelectedItem(this.selectedValue);
    },
    methods: {
        syncSelectedItem(value) {
            const availableLabels = this.mergedOptions.items.labels.map((label) => label.name);

            if (typeof value === "string" && availableLabels.includes(value)) {
                this.selectedItem = value;
                return;
            }

            if (this.mergedOptions.items.preSelected && availableLabels.includes(this.mergedOptions.items.preSelected)) {
                this.selectedItem = this.mergedOptions.items.preSelected;
                return;
            }

            this.selectedItem = availableLabels[0] || "unknown";
        },
        getSelectedItemStyle(label) {
            return {
                ...this.itemStyle,
                backgroundColor: label.backgroundColor || this.mergedOptions.layout.selectedBackgroundColor,
                color: label.color || this.mergedOptions.layout.selectedColor,
                fontWeight: this.mergedOptions.layout.fontWeightSelected,
            };
        },
        select(value, event) {
            if (this.isDisabled || this.selectedItem === value) {
                return;
            }

            this.selectedItem = value;
            this.$emit("input", value);
            this.$emit("update:modelValue", value);
            this.$emit("selected", true);
            this.$emit("change", {
                srcEvent: event,
                value,
            });
        },
    },
    watch: {
        options: {
            deep: true,
            handler() {
                this.syncSelectedItem(this.selectedValue);
            },
        },
        selectedValue(nextValue) {
            this.syncSelectedItem(nextValue);
        },
    },
};
</script>

<style scoped>
.segmented-toggle {
    align-items: stretch;
    display: inline-flex;
    overflow: hidden;
    padding: 0;
}

.segmented-toggle.is-disabled {
    opacity: 0.6;
}

.segmented-toggle__item {
    align-items: center;
    appearance: none;
    border-color: var(--segmented-border-color);
    border-style: solid;
    border-width: 1px 1px 1px 0;
    border-radius: 0;
    box-shadow: none;
    cursor: pointer;
    display: inline-flex;
    flex: 1 1 auto;
    height: var(--segmented-height);
    justify-content: center;
    line-height: 1;
    margin: 0;
    min-width: 0;
    padding: 0 var(--segmented-padding);
    transition: background-color var(--segmented-transition) ease, color var(--segmented-transition) ease;
    white-space: nowrap;
}

.segmented-toggle__item:first-child {
    border-left-width: 1px;
    border-radius: 5px 0 0 5px;
}

.segmented-toggle__item:last-child {
    border-radius: 0 5px 5px 0;
}

.segmented-toggle.is-square .segmented-toggle__item {
    border-radius: 0;
}

.segmented-toggle__item:disabled {
    cursor: not-allowed;
}
</style>
